export class OnboardingApi {
  constructor(sessionId) {
    this.sessionId = sessionId
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
  }

  async post(endpoint, data = {}) {
    const url = `/onboarding/sessions/${this.sessionId}/${endpoint}`
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken || ""
      },
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${await response.text()}`)
    }

    return response.json()
  }

  async get(endpoint) {
    const url = `/onboarding/sessions/${this.sessionId}/${endpoint}`
    const response = await fetch(url, {
      headers: { Accept: "application/json" }
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    return response.json()
  }

  async patch(path, data = {}) {
    const response = await fetch(path, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken || ""
      },
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    return response.json()
  }

  // Its own request rather than `post` above, because that one throws
  // `HTTP 422: {"error":"..."}` — a string built for a console, not for a
  // person. The server's sentence is the whole value of this endpoint's
  // failure paths, so it is carried out intact on `userMessage`: a blank
  // address, an unreachable one and a page with nothing on it are three
  // different problems and the person is told which one they have.
  //
  // Answers { website_analysis, suggestions } — one call, both panels.
  async analyzeWebsite(website) {
    const url = `/onboarding/sessions/${this.sessionId}/analyse_website`
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken || ""
      },
      body: JSON.stringify({ website })
    })

    let body = {}
    try {
      body = await response.json()
    } catch (_) {
      body = {}
    }

    if (!response.ok) {
      const error = new Error(`HTTP ${response.status}`)
      error.userMessage = body.error
      error.status = response.status
      throw error
    }

    return body
  }

  async analyzeLinkedin(linkedinUrl) {
    return this.post("analyse_linkedin", { linkedin_url: linkedinUrl })
  }

  async generateIcp(icpInputs) {
    return this.post("generate_icp", { icp_inputs: icpInputs })
  }

  // The explicit replacement of a finished set (KI-469): archives the session's
  // current ICPs, then generates. Its own endpoint, so generateIcp can never
  // archive anything.
  async regenerateIcp(icpInputs) {
    return this.post("regenerate_icp", { icp_inputs: icpInputs })
  }

  async getIcpStatus() {
    return this.get("icp_status")
  }

  async updatePersonaStatus(personaId, status) {
    const url = `/onboarding/sessions/${this.sessionId}/persona_drafts/${personaId}`
    return this.patch(url, { persona_draft: { status } })
  }

  async updatePersona(personaId, data) {
    const url = `/onboarding/sessions/${this.sessionId}/persona_drafts/${personaId}`
    return this.patch(url, { persona_draft: data })
  }

  async summarizeIcp(icpInputs) {
    return this.post("summarise_icp", { icp_inputs: icpInputs })
  }

  async launch(icpInputs) {
    return this.post("launch", icpInputs)
  }
}
